import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useJoyride } from 'react-joyride';

import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { useLangStore } from '@app/dashboard/store/lang';
import { updateDashboardGuideStatus } from '@app/dashboard/services/inner_layout/dashboard_guide';
import { addToLocalStorage } from '@app/dashboard/utils/manageLocalStorage';

import { Step } from './Step';
import { WelcomeStep } from './WelcomeStep';

import {
  CustomTooltip,
  getJoyRideElementId,
  StepsContent,
  StepsIcons
} from './utils';


export const DashboardGuide = () => {
  const { t } = useTranslation();
  const { lang } = useLangStore();

  const {
    mutate
  } = useMutation({
    mutationKey: [QUERY_KEYS.UPDATE_DASHBOARD_GUIDE_STATUS],
    mutationFn: updateDashboardGuideStatus,
    onSuccess: () => {
      addToLocalStorage(LOCAL_STORAGE_KEYS.SHOW_DASHBOARD_TOUR, false);
    },
  });

  const [startJourney, setStartJourney] = useState(false);

  const featuresList = t('innerLayout.dashboardGuide.steps.info', { returnObjects: true });

  const steps = featuresList.map((
    feat: { label: string, brief: string, title: string },
    i: number
  ) => {
    const { node, placement, floatingOptions } = StepsContent(t, lang)[i];

    return {
      content: <Step
        key={i}
        step={i + 1}
        totalNumberOfSteps={featuresList.length}
        featureName={feat.label}
        featureIcon={StepsIcons[i]}
        desc={feat.brief}
        label={feat.title}
        content={node}
      />,
      target: `#${getJoyRideElementId(i + 1)}`,
      placement,
      floatingOptions
    }
  });


  const { on, Tour } = useJoyride(
    {
      continuous: true,
      run: startJourney,
      steps: steps as any,
      tooltipComponent: CustomTooltip,
      options: {
        skipBeacon: true,
        blockTargetInteraction: true,
        spotlightRadius: 15
      },
      floatingOptions: {
        hideArrow: true,
      },
      styles: {
        overlay: {
          opacity: 0,
        },
        floater: {
          maxHeight: 'calc(100dvh - 5.5rem)',
        }
      }
    }
  );

  const getStartedHandler = () => setStartJourney(true);

  const onDismissHandler = () => {
    document.documentElement.style.overflow = '';
    mutate();
  };


  useEffect(() => {
    const cleanupTourStart = on('tour:start', () => {
      window.dispatchEvent(new CustomEvent('joyride:tour:start'));
      document.documentElement.style.overflow = 'hidden';
    });

    const cleanupTourEnd = on('tour:end', () => {
      window.dispatchEvent(new CustomEvent('joyride:tour:end'));
      onDismissHandler();
    });

    return () => {
      cleanupTourStart();
      cleanupTourEnd();
    };
  }, [on]);


  return (
    <>
      <WelcomeStep
        onNextHandler={getStartedHandler}
        onDismissHandler={onDismissHandler}
      />

      {Tour}
    </>
  )
};